import { useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useNavigate, useLocation } from "react-router";
import {
  setSelectedSites,
  useSelectedSites,
} from "@medoma/shared-employee/selectedSites";
import { sitesQueryOptions } from "@/api/Patients";
import DropdownMenu from "@/components/DropdownMenu/DropdownMenu";
import { Popover } from "@/components/Popover/Popover";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import NoResults from "@/components/NoResults/NoResults";
import styles from "./SiteSwitcher.module.scss";

const SiteDisplayName = ({ site }: { site: string }) => (
  <div className={styles.siteDisplayName}>{site}</div>
);

type Section =
  | "patients"
  | "activities"
  | "planning"
  | "capacity"
  | "equipment"
  | "shifts"
  | "reporting"
  | "chat";

const SECTION_ROUTES: Record<Section, string> = {
  patients: "patients/admitted",
  activities: "activities",
  planning: "planning",
  capacity: "capacity",
  equipment: "equipment",
  shifts: "shifts",
  reporting: "reporting",
  chat: "chat",
} as const;

const DEFAULT_NAVIGATION_PATH = "/commandcenter/patients/admitted";

const isValidSection = (section: string): section is Section => {
  return section in SECTION_ROUTES;
};

/**
 * Determines the navigation target when switching sites.
 * Always navigates to the first-level (overview) of the current section.
 *
 * @param pathname - Current location pathname
 * @returns The target path to navigate to
 */
const getNavigationTargetOnSiteSwitch = (pathname: string): string => {
  // Parse the pathname: /{baseApp}/{section}/{...rest}
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length < 2) {
    // Fallback if we can't determine the structure
    return DEFAULT_NAVIGATION_PATH;
  }

  const baseApp = segments[0]; // "commandcenter" or "center"

  if (baseApp === "template-manager") {
    return "/template-manager/templates";
  }

  const section = segments[1];

  if (section && isValidSection(section)) {
    return `/${baseApp}/${SECTION_ROUTES[section]}`;
  }

  // Fallback to patients overview
  return `/${baseApp}/patients/admitted`;
};

export const SiteSwitcher = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) {
    throw new Error("Should have user when rendering site switcher");
  }

  const selectedSites = useSelectedSites(user.profile.sub);
  if (!selectedSites) {
    throw new Error("Should have selected site when showing site menu");
  }

  const { data: sites, isPending, isError } = useQuery(sitesQueryOptions);

  const selectedSitesDisplayName = selectedSites.map(
    (selectedSiteId) =>
      sites?.find(({ id }) => id === selectedSiteId)?.displayName ??
      selectedSiteId,
  );

  if (isPending) {
    return (
      <Popover
        trigger={{
          custom: (
            <SiteDisplayName site={selectedSitesDisplayName.join(", ")} />
          ),
          ariaLabel: t`Välj enhet`,
        }}
      >
        <Loading message={t`Hämtar tillgängliga enheter`} />
      </Popover>
    );
  }

  if (isError) {
    return (
      <Popover
        trigger={{
          custom: (
            <SiteDisplayName site={selectedSitesDisplayName.join(", ")} />
          ),
          ariaLabel: t`Välj enhet`,
        }}
      >
        <ErrorMessage
          message={t`Kunde inte hämta tillgängliga enheter. Kontakta support om problemet består.`}
        />
      </Popover>
    );
  }

  if (sites.length <= 1) {
    return (
      <Popover
        trigger={{
          custom: (
            <SiteDisplayName site={selectedSitesDisplayName.join(", ")} />
          ),
          ariaLabel: t`Välj enhet`,
        }}
      >
        <NoResults message={t`Du har inga andra tillgängliga enheter.`} />
      </Popover>
    );
  }

  return (
    <DropdownMenu
      trigger={{
        custom: (
          <SiteDisplayName
            site={selectedSitesDisplayName?.join(", ") ?? t`Välj enhet`}
          />
        ),
        ariaLabel: t`Välj enhet`,
      }}
    >
      {sites.map((site) => (
        <DropdownMenu.Item
          action={() => {
            setSelectedSites([site.id], user.profile.sub);

            // Navigate to the overview of the current section before resetting queries
            const targetPath = getNavigationTargetOnSiteSwitch(
              location.pathname,
            );
            void navigate(targetPath);

            void queryClient.resetQueries();
          }}
          key={site.id}
          content={site.displayName}
        />
      ))}
    </DropdownMenu>
  );
};
